import { Icons } from "../../shared/ui";
import type { OperationsSnapshot } from "./operations";
import type { AdminSectionId } from "./adminRoute";
import type { ShellTranslate } from "./useShellT";

export type Glyph = typeof Icons.Home;

export type SectionGroupId = "monitoring" | "people" | "assistant" | "access";

export interface SectionGroup {
  id: SectionGroupId;
  sections: readonly AdminSectionId[];
}

/** Display order of the master list; every section belongs to exactly one. */
export const SECTION_GROUPS: readonly SectionGroup[] = [
  { id: "monitoring", sections: ["overview", "load", "logs"] },
  { id: "people", sections: ["users", "payments"] },
  { id: "assistant", sections: ["models", "inspector", "feedback"] },
  { id: "access", sections: ["access", "audit"] },
];

export const SECTION_GLYPHS: Record<AdminSectionId, Glyph> = {
  overview: Icons.Home,
  load: Icons.Server,
  models: Icons.Sparkles,
  inspector: Icons.Search,
  feedback: Icons.ThumbUp,
  users: Icons.User,
  logs: Icons.Clock,
  payments: Icons.Grid,
  access: Icons.Lock,
  audit: Icons.Draft,
};

export function groupLabel(t: ShellTranslate, id: SectionGroupId): string {
  switch (id) {
    case "monitoring":
      return t("admin.groups.monitoring");
    case "people":
      return t("admin.groups.people");
    case "assistant":
      return t("admin.groups.assistant");
    case "access":
      return t("admin.groups.access");
  }
}

export function sectionLabel(t: ShellTranslate, id: AdminSectionId): string {
  switch (id) {
    case "overview":
      return t("admin.tabs.overview");
    case "load":
      return t("admin.tabs.load");
    case "models":
      return t("admin.tabs.models");
    case "inspector":
      return t("admin.tabs.inspector");
    case "feedback":
      return t("admin.tabs.feedback");
    case "users":
      return t("admin.tabs.users");
    case "logs":
      return t("admin.tabs.logs");
    case "payments":
      return t("admin.tabs.payments");
    case "access":
      return t("admin.tabs.access");
    case "audit":
      return t("admin.tabs.audit");
  }
}

export interface SectionBadge {
  count: number;
  tone: "neutral" | "warning";
}

/** Count shown on a list row; null hides the badge (also when the count is 0). */
export function sectionBadge(
  id: AdminSectionId,
  snapshot: OperationsSnapshot,
): SectionBadge | null {
  if (id === "overview") {
    return snapshot.incident_count > 0
      ? { count: snapshot.incident_count, tone: "warning" }
      : null;
  }
  if (id === "load") {
    return snapshot.active_jobs > 0
      ? { count: snapshot.active_jobs, tone: "neutral" }
      : null;
  }
  return null;
}
